// Forecast slaughter chart (bottom-right): monthly volume with a kt ↔ MNOK
// toggle, a per-site click highlight, and an FX (NOK) scenario slider. Value =
// volume × forecast price × FX factor. The FX slider is a *scenario* tool, not
// a prediction: USD/NOK is ~unforecastable, but its medium-run pass-through to
// NOK salmon price is ~0.45 (estimated on YoY changes, trend-removed), so
// "NOK 10% weaker → price ~+4.5%". Reads /v1/farms/cycle + /v1/salmon/price.

export type HarvestItem = {
  harvest_year: number | null;
  harvest_month: number | null;
  harvest_t: number | null;
};

const MONTHS_NO = ["jan", "feb", "mar", "apr", "mai", "jun", "jul", "aug", "sep", "okt", "nov", "des"];
export const MONTHS_NO_FULL = [
  "januar", "februar", "mars", "april", "mai", "juni",
  "juli", "august", "september", "oktober", "november", "desember",
];
const HORIZON = 18; // covers cohorts stocked recently (harvest ≈ +18 mo)
const FX_PASSTHROUGH = 0.45; // d(price%) / d(NOK weakening %), medium-run estimate

type ValueMode = "kt" | "mnok";

function monthIndex(y: number, m: number): number {
  return y * 12 + (m - 1);
}

let lastItems: HarvestItem[] = [];
let anchor = 0;
let highlightIdx: number | null = null;
let priceByMonth = new Map<string, number>();
let valueMode: ValueMode = "kt";
let fxStrength = 0; // % NOK stronger(+)/weaker(−) vs today
let fxNote = "";   // rate-path-implied anchor explanation

// Where the chart skeleton lives. Defaults to <body> (legacy floating panel);
// the analytics "Biomasse & verdi" board moves it into itself via mountHarvestChart.
let mountParent: HTMLElement | null = null;

/**
 * Mount (or move) the chart into a container — used by the analytics panel so
 * the slaughter forecast lives in the right-hand boards instead of floating.
 * Returns the chart element so the caller can place it precisely.
 */
export function mountHarvestChart(parent: HTMLElement): HTMLElement {
  mountParent = parent;
  const panel = ensurePanel();
  if (panel.parentElement !== parent) parent.appendChild(panel);
  panel.classList.add("is-embedded");
  return panel;
}

/** Forecast NOK/kg for a (year, month), or null beyond the price horizon. */
export function getPrice(y: number, m: number): number | null {
  return priceByMonth.get(`${y}-${m}`) ?? null;
}

/** Price multiplier from the FX scenario (NOK stronger → lower NOK price). */
export function getFxFactor(): number {
  return 1 + FX_PASSTHROUGH * (-fxStrength / 100);
}

/** Seed the slider with the rate-path-implied NOK move (covered parity). */
export function setImpliedFx(pct: number, note: string): void {
  fxStrength = Math.round(pct);
  fxNote = note;
  const panel = document.getElementById("harvest-chart");
  if (!panel) return; // state will be applied when the panel is built
  const sl = panel.querySelector<HTMLInputElement>(".hc-fxslider");
  if (sl) sl.value = String(fxStrength);
  updateFxReadout(panel);
  if (valueMode === "mnok") updateBars();
}

export function renderHarvestChart(items: HarvestItem[], asOf: Date, prices?: Map<string, number>): void {
  lastItems = items;
  anchor = monthIndex(asOf.getFullYear(), asOf.getMonth() + 1);
  if (prices) priceByMonth = prices;
  ensurePanel();
  updateBars();
}

export function highlightHarvestMonth(y: number | null, m: number | null): void {
  if (y == null || m == null) {
    highlightIdx = null;
  } else {
    let idx = monthIndex(y, m) - anchor;
    if (idx < 0) idx = 0;
    highlightIdx = idx < HORIZON ? idx : null;
  }
  updateBars();
}

function ymAt(i: number): { y: number; m: number } {
  const idx = anchor + i;
  return { y: Math.floor(idx / 12), m: (idx % 12) + 1 };
}

// Persistent skeleton — built once so the FX slider survives bar redraws.
function ensurePanel(): HTMLElement {
  const existing = document.getElementById("harvest-chart");
  if (existing) return existing;
  const panel = document.createElement("div");
  panel.id = "harvest-chart";
  panel.className = "panel harvest-chart";
  panel.innerHTML =
    `<div class="hc-title">Anslått slakt` +
    `<span class="hc-units"><button class="hc-u" data-u="kt">kt</button>` +
    `<button class="hc-u" data-u="mnok">MNOK</button></span></div>` +
    `<div class="hc-bars"></div><div class="hc-foot"></div>` +
    `<div class="hc-fx"><div class="hc-fxlabel">Valuta-scenario · NOK ` +
    `<b class="hc-fxval">0 %</b> → pris <b class="hc-fxp">0 %</b></div>` +
    `<input type="range" min="-15" max="15" step="1" value="${fxStrength}" class="hc-fxslider">` +
    `<div class="hc-fxnote"></div></div>`;
  (mountParent ?? document.body).appendChild(panel);
  panel.querySelectorAll<HTMLButtonElement>(".hc-u").forEach((b) =>
    b.addEventListener("click", () => {
      valueMode = (b.dataset.u as ValueMode) ?? "kt";
      updateBars();
    }),
  );
  const sl = panel.querySelector<HTMLInputElement>(".hc-fxslider");
  sl?.addEventListener("input", () => {
    fxStrength = Number(sl.value);
    updateFxReadout(panel);
    if (valueMode === "mnok") updateBars();
  });
  return panel;
}

function updateFxReadout(panel: HTMLElement): void {
  const pricePct = (getFxFactor() - 1) * 100;
  const fxv = panel.querySelector(".hc-fxval");
  const fxp = panel.querySelector(".hc-fxp");
  const note = panel.querySelector(".hc-fxnote");
  if (fxv) fxv.textContent = `${fxStrength > 0 ? "+" : ""}${fxStrength} %`;
  if (fxp) fxp.textContent = `${pricePct > 0 ? "+" : ""}${pricePct.toFixed(1)} %`;
  if (note) note.textContent = fxNote;
}

function updateBars(): void {
  const panel = ensurePanel();
  const tonnes = new Array<number>(HORIZON).fill(0);
  const counts = new Array<number>(HORIZON).fill(0);
  for (const it of lastItems) {
    if (!it.harvest_t || it.harvest_year == null || it.harvest_month == null) continue;
    let idx = monthIndex(it.harvest_year, it.harvest_month) - anchor;
    if (idx < 0) idx = 0;
    if (idx >= HORIZON) continue;
    tonnes[idx] = (tonnes[idx] ?? 0) + it.harvest_t;
    counts[idx] = (counts[idx] ?? 0) + 1;
  }
  const fxf = getFxFactor();
  const disp = tonnes.map((t, i) => {
    if (valueMode === "kt") return t / 1000;
    const { y, m } = ymAt(i);
    const pr = priceByMonth.get(`${y}-${m}`);
    return pr ? (t * pr * fxf) / 1000 : 0;
  });
  const max = Math.max(1, ...disp);
  const peak = disp.indexOf(max);
  const unit = valueMode === "kt" ? "kt" : "MNOK";

  const bars = disp
    .map((v, i) => {
      const { y, m } = ymAt(i);
      const h = Math.round(3 + (v / max) * 70);
      const lbl = MONTHS_NO[(anchor + i) % 12] ?? "";
      const showYr = m === 1 || i === 0;
      const vtxt = valueMode === "kt" ? v.toFixed(1) : Math.round(v).toString();
      let cls = "hc-bar";
      if (i === peak && v > 0) cls += " is-peak";
      if (i === highlightIdx) cls += " is-selected";
      return (
        `<div class="${cls}" title="${lbl} ${y}: ${vtxt} ${unit} · ${counts[i]} anlegg">` +
        `<div class="hc-fill" style="height:${h}px"></div>` +
        `<div class="hc-x"><span class="hc-mo">${lbl}</span>` +
        `<span class="hc-yr">${showYr ? `'${String(y).slice(2)}` : ""}</span></div>` +
        `</div>`
      );
    })
    .join("");

  const total = disp.reduce((a, b) => a + b, 0);
  const totTxt = valueMode === "kt"
    ? `~${total.toFixed(0)} kt`
    : `~${(total / 1000).toFixed(1)} mrd kr`;
  const barsEl = panel.querySelector(".hc-bars");
  const footEl = panel.querySelector(".hc-foot");
  if (barsEl) barsEl.innerHTML = bars;
  if (footEl) {
    footEl.textContent = `${totTxt} neste ${HORIZON} mnd · anslag` +
      (valueMode === "mnok" ? " (volum × pris × valuta)" : "");
  }
  panel.querySelectorAll<HTMLButtonElement>(".hc-u").forEach((b) =>
    b.classList.toggle("is-active", b.dataset.u === valueMode));
  const fx = panel.querySelector<HTMLElement>(".hc-fx");
  if (fx) fx.style.display = valueMode === "mnok" ? "block" : "none";
  updateFxReadout(panel);
}

/** Where a site's harvest month sits among all forecasted sites. */
export function harvestPeers(
  items: HarvestItem[],
  y: number,
  m: number,
): { beforePct: number; same: number; total: number } {
  const dates = items
    .filter((it) => it.harvest_t && it.harvest_year != null && it.harvest_month != null)
    .map((it) => monthIndex(it.harvest_year as number, it.harvest_month as number));
  const me = monthIndex(y, m);
  const before = dates.filter((d) => d < me).length;
  const same = dates.filter((d) => d === me).length;
  return {
    beforePct: dates.length ? Math.round((100 * before) / dates.length) : 0,
    same: Math.max(0, same - 1),
    total: dates.length,
  };
}
